import type {
  QuestionEditorState,
  QuestionUpsertRequest,
  QuestionDetail,
} from '../types';
import { INPUT_FORMAT_TYPES, LENGTH_VALIDATED_TYPES } from '../constants';

// Maps API QuestionDetail onto the editor's mutable form state
export function detailToEditorState(
  detail: QuestionDetail
): QuestionEditorState {
  return {
    questionTitle: detail.questionTitle,
    questionTypeId: detail.questionTypeId,
    required: detail.required,
    active: detail.active,
    surveyQuestionDbCode: detail.surveyQuestionDbCode ?? '',
    inputFormat: detail.inputFormat ?? '',
    minLength: detail.minLength !== null ? String(detail.minLength) : '',
    maxLength: detail.maxLength !== null ? String(detail.maxLength) : '',
    errorMessage: detail.errorMessage ?? '',
    helpText: detail.helpText ?? '',
    helpLink: detail.helpLink ?? '',
    helpLinkLabel: detail.helpLinkLabel ?? '',
    options: detail.options.map((o) => {
      return {
        questionOptionId: o.questionOptionId,
        optionTitle: o.optionTitle,
        orderId: o.orderId,
        active: o.active,
      };
    }),
  };
}

// Maps editor form state onto the API upsert payload
export function editorStateToRequest(
  state: QuestionEditorState
): QuestionUpsertRequest {
  const typeId = state.questionTypeId ?? 0;
  const supportsLength = LENGTH_VALIDATED_TYPES.has(typeId);
  const supportsInputFormat = INPUT_FORMAT_TYPES.has(typeId);
  return {
    questionTitle: state.questionTitle.trim(),
    questionTypeId: typeId,
    required: state.required,
    active: state.active,
    surveyQuestionDbCode: state.surveyQuestionDbCode.trim() || null,
    inputFormat: supportsInputFormat ? state.inputFormat.trim() || null : null,
    minLength: supportsLength
      ? state.minLength
        ? parseInt(state.minLength, 10)
        : null
      : null,
    maxLength: supportsLength
      ? state.maxLength
        ? parseInt(state.maxLength, 10)
        : null
      : null,
    errorMessage: supportsLength ? state.errorMessage.trim() || null : null,
    helpText: state.helpText.trim() || null,
    helpLink: state.helpLink.trim() || null,
    helpLinkLabel: state.helpLinkLabel.trim() || null,
    options: state.options.map((o, i) => {
      return {
        ...(o.questionOptionId !== undefined
          ? { questionOptionId: o.questionOptionId }
          : {}),
        optionTitle: o.optionTitle,
        orderId: o.orderId ?? i + 1,
        active: o.active !== false,
      };
    }),
  };
}

export function emptyEditorState(): QuestionEditorState {
  return {
    questionTitle: '',
    questionTypeId: null,
    required: false,
    active: true,
    surveyQuestionDbCode: '',
    inputFormat: '',
    minLength: '',
    maxLength: '',
    errorMessage: '',
    helpText: '',
    helpLink: '',
    helpLinkLabel: '',
    options: [],
  };
}
